<template>
  <div class="pdf-viewer-container">
    <!-- Sección de Seleccionar Firmante -->
    <div class="card mb-3">
      <div class="card-body">
        <h6 class="mb-3 d-flex align-items-center gap-2">
          <i class="fas fa-users text-primary"></i>
          Seleccionar Firmante
        </h6>
        <!-- Contenedor para tarjetas de firmantes (renderizado manual) -->
        <div ref="firmantesCardsContainer" class="d-flex flex-wrap gap-2">
          <!-- Las tarjetas se renderizan manualmente mediante renderizarTarjetasFirmantes() -->
        </div>
        
        <button class="btn btn-secondary btn-sm mt-3" @click="limpiarSeleccion">
          <i class="fas fa-trash-alt me-2"></i>
          Limpiar selección
        </button>
      </div>
    </div>
    
    <!-- Controles de navegación y documento -->
    <div v-if="cargandoPdf" class="text-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando PDF...</span>
      </div>
      <p class="mt-2 text-muted">Cargando documento...</p>
    </div>
    
    <div v-else-if="pdfError" class="alert alert-danger">
      <i class="fas fa-exclamation-triangle me-2"></i>
      Error al cargar el PDF: {{ pdfError }}
    </div>
    
    <div v-else-if="pdfCargado">
      <div class="navigation-controls mb-3 d-flex justify-content-between align-items-center">
        <button class="btn btn-sm btn-outline-secondary" 
          :disabled="paginaActual === 1" 
          @click="cambiarPagina(paginaActual - 1)">
          <i class="fas fa-chevron-left me-1"></i>
          Anterior
        </button>
        <span class="text-muted">
          Página {{ paginaActual }} de {{ totalPaginas }}
        </span>
        <button class="btn btn-sm btn-outline-secondary" 
          :disabled="paginaActual === totalPaginas" 
          @click="cambiarPagina(paginaActual + 1)">
          Siguiente
          <i class="fas fa-chevron-right ms-1"></i>
        </button>
      </div>
          
      <!-- Contenedor del PDF con overlay -->
      <div class="pdf-container" style="position: relative; display: inline-block;">
        <img ref="imgPdf" 
          class="img-pdf"
          style="border: 3px solid darkgray; border-radius: 5px; max-height: 600px; width: auto; display: block;"
          :src="pages[paginaActual - 1]?.dataUrl"
          alt="Página del PDF" />
        
        <!-- Overlay para recuadros de firmas -->
        <div ref="overlayContainer" 
          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 10;">
        </div>
        
        <!-- Overlay para eventos de mouse -->
        <div ref="interactionOverlay"
          @mousedown="onMouseDown"
          @mousemove="onMouseMove"
          @mouseup="onMouseUp"
          @mouseleave="onMouseLeave"
          style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: auto; z-index: 5; cursor: crosshair;">
        </div>
        
        <!-- Preview del rectángulo mientras se dibuja -->
        <div v-if="dibujandoRectangulo" 
          ref="previewRectangulo"
          style="position: absolute; border: 2px dashed #409eff; background: rgba(64, 158, 255, 0.1); pointer-events: none; z-index: 15;"
          :style="{
            left: previewRect.x + 'px',
            top: previewRect.y + 'px',
            width: previewRect.width + 'px',
            height: previewRect.height + 'px'
          }">
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import axiosInstance from '@/axios'

const props = defineProps({
  firmantes: {
    type: Array,
    required: true
  },
  registroSeleccionado: {
    type: Object,
    required: true
  },
  esquemaId: {
    type: [String, Number],
    required: false
  },
  showNotification: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['posicion-guardada', 'posiciones-limpias'])

// Estados del PDF
const pdfCargado = ref(false)
const cargandoPdf = ref(false)
const pdfError = ref(null)
const pdfDoc = ref(null)
const pages = ref([])
const totalPaginas = ref(0)
const paginaActual = ref(1)

// Estados de selección
const numFirmante = ref(0)
const posicionesMap = ref(new Map()) // firmanteId -> {x, y, ancho, alto, pagina, px, py, anchoMm, altoMm}

// Estados para dibujar rectángulo
const dibujandoRectangulo = ref(false)
const inicioRectangulo = ref({ x: 0, y: 0 })
const previewRect = ref({ x: 0, y: 0, width: 0, height: 0 })

// Referencias DOM
const imgPdf = ref(null)
const overlayContainer = ref(null)
const interactionOverlay = ref(null)
const firmantesCardsContainer = ref(null)

// Preview deshabilitado - ya no se usa

// Colores para los marcadores
const colores = [
  "#74b9ff", "#a29bfe", "#55efc4", "#81ecec", "#ff9ff3",
  "#fd79a8", "#ff7675", "#b2bec3", "#fab1a0", "#5352ed"
]

// NOTA: Las funciones tienePosicion, getFirmanteCardStyle y getFirmanteIconStyle
// ya no son necesarias porque el renderizado de tarjetas es manual

// NOTA: getPosicionFirmanteActual ya no es necesario porque se eliminó el botón "Guardar posición"

// Funciones del PDF
const getPdfjs = () => {
  if (typeof window === 'undefined' || !window.pdfjsLib) {
    throw new Error('PDF.js no está cargado. Verifica que el script esté en index.html')
  }
  return window.pdfjsLib
}

const base64ToArrayBuffer = (base64) => {
  const binaryString = window.atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes.buffer
}

const cargarPdf = async () => {
  cargandoPdf.value = true
  pdfError.value = null
  pages.value = []
  
  try {
    const pdfjs = getPdfjs()
    
    const seleccionado = props.registroSeleccionado?.value || props.registroSeleccionado
    if (!seleccionado?.id) {
      throw new Error('No hay documento seleccionado')
    }
    
    // Obtener el esquemaId desde props o desde registroSeleccionado como fallback
    const esquemaId = props.esquemaId || props.registroSeleccionado?.esquema_id || props.registroSeleccionado?.esquema?.id
    if (!esquemaId) {
      throw new Error('No se pudo obtener el ID del esquema')
    }
    
    const response = await axiosInstance.get(`api/ficheroRegistro/${esquemaId}/${seleccionado.id}`)
    
    if (!response.data?.base64) {
      throw new Error('No se pudo obtener el archivo PDF')
    }
    
    const arrayBuffer = base64ToArrayBuffer(response.data.base64)
    const loadingTask = pdfjs.getDocument({ data: arrayBuffer })
    pdfDoc.value = await loadingTask.promise
    totalPaginas.value = pdfDoc.value.numPages
    
    // Cargar todas las páginas
    for (let i = 1; i <= totalPaginas.value; i++) {
      const page = await pdfDoc.value.getPage(i)
      const viewport = page.getViewport({ scale: 1.5 })
      
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      canvas.height = viewport.height
      canvas.width = viewport.width
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise
      
      // Obtener el viewport original (sin escala) para conversión de coordenadas
      const viewportOriginal = page.getViewport({ scale: 1.0 })
      
      pages.value.push({
        pageNum: i,
        dataUrl: canvas.toDataURL('image/png'),
        viewport: viewport, // Viewport renderizado (con escala)
        viewportOriginal: viewportOriginal // Viewport original (sin escala, en puntos)
      })
    }
    
    pdfCargado.value = true
    paginaActual.value = 1
    await nextTick()
    renderizarMarcadores()
  } catch (error) {
    console.error('Error al cargar el PDF:', error)
    pdfError.value = error.message || 'Error desconocido al cargar el PDF'
  } finally {
    cargandoPdf.value = false
  }
}

const cambiarPagina = (nuevaPagina) => {
  if (nuevaPagina >= 1 && nuevaPagina <= totalPaginas.value) {
    paginaActual.value = nuevaPagina
    nextTick(() => {
      renderizarMarcadores()
    })
  }
}

// Funciones de renderizado
const renderizarMarcadores = () => {
  if (!overlayContainer.value || !imgPdf.value) return
  
  // Limpiar marcadores existentes de forma segura
  while (overlayContainer.value.firstChild) {
    overlayContainer.value.removeChild(overlayContainer.value.firstChild)
  }
  
  // Renderizar marcadores usando el mapa de posiciones
  props.firmantes.forEach((item, idx) => {
    const firmanteId = item.firmante.id
    const posicion = posicionesMap.value.get(firmanteId)
    
    if (posicion && posicion.pagina === paginaActual.value) {
      // Crear contenedor del rectángulo
      const container = document.createElement('div')
      container.setAttribute('data-firmante-id', firmanteId)
      container.className = 'firma-marcador-container'
      
      // Obtener el nombre del usuario
      const nombreCompleto = `${item.firmante.name || ''} ${item.firmante.username || ''}`.trim() || 
                            `${item.firmante.nombre || ''} ${item.firmante.apellidos || ''}`.trim()
      const textoMostrar = nombreCompleto || `Firmante ${item.ordenFirma || (idx + 1)}`
      const color = colores[idx % colores.length]
      
      // Rectángulo de fondo
      const rectangulo = document.createElement('div')
      Object.assign(rectangulo.style, {
        position: 'absolute',
        backgroundColor: 'transparent',
        border: `2px solid ${color}`,
        borderRadius: '4px',
        top: `${posicion.y}px`,
        left: `${posicion.x}px`,
        width: `${posicion.ancho || 120}px`,
        height: `${posicion.alto || 28}px`,
        zIndex: '100',
        pointerEvents: 'none',
        boxSizing: 'border-box'
      })
      
      // Etiqueta con el nombre
      const etiqueta = document.createElement('div')
      Object.assign(etiqueta.style, {
        position: 'absolute',
        backgroundColor: color,
        color: 'white',
        padding: '2px 6px',
        borderRadius: '3px',
        fontSize: '10px',
        fontWeight: '600',
        top: `${posicion.y - 20}px`,
        left: `${posicion.x}px`,
        zIndex: '101',
        pointerEvents: 'none',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        maxWidth: '200px',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      })
      etiqueta.textContent = textoMostrar
      
      container.appendChild(rectangulo)
      container.appendChild(etiqueta)
      
      // Añadir al contenedor de forma segura
      try {
        overlayContainer.value.appendChild(container)
      } catch (error) {
        console.warn('[renderizarMarcadores] Error al añadir marcador:', error.message)
      }
    }
  })
}

// Renderizar tarjetas de firmantes manualmente (sin v-for para evitar conflictos con Vue)
const renderizarTarjetasFirmantes = () => {
  if (!firmantesCardsContainer.value) return
  
  // Limpiar tarjetas existentes
  while (firmantesCardsContainer.value.firstChild) {
    firmantesCardsContainer.value.removeChild(firmantesCardsContainer.value.firstChild)
  }
  
  // Renderizar cada tarjeta manualmente
  props.firmantes.forEach((item, index) => {
    const card = document.createElement('div')
    card.className = 'firmante-card'
    card.setAttribute('data-firmante-index', index)
    
    const isSelected = index === numFirmante.value
    const hasPosition = !!posicionesMap.value.get(item.firmante.id)
    
    // Estilos de la tarjeta
    Object.assign(card.style, {
      minWidth: '200px',
      flex: '1 1 auto',
      padding: '12px 16px',
      borderRadius: '12px',
      border: '2px solid',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      borderColor: isSelected ? '#409eff' : (hasPosition ? '#10b981' : '#e5e7eb'),
      backgroundColor: isSelected ? '#ecf5ff' : (hasPosition ? '#f0fdf4' : '#ffffff')
    })
    
    // Icono de firma
    const iconSignature = document.createElement('i')
    iconSignature.className = 'fas fa-signature'
    Object.assign(iconSignature.style, {
      color: hasPosition ? '#10b981' : '#ef4444',
      fontSize: '18px'
    })
    card.appendChild(iconSignature)
    
    // Nombre del firmante
    const nombreSpan = document.createElement('span')
    nombreSpan.className = 'flex-grow-1'
    nombreSpan.textContent = `${item.firmante.name || ''}`.trim()
    card.appendChild(nombreSpan)
    
    // Icono de estado (check o circle)
    const iconStatus = document.createElement('i')
    if (hasPosition) {
      iconStatus.className = 'fas fa-check-circle text-success'
    } else {
      iconStatus.className = 'fas fa-circle text-muted'
    }
    card.appendChild(iconStatus)
    
    // Event listener para el click
    card.addEventListener('click', () => {
      seleccionarFirmante(index)
      // Re-renderizar para actualizar estilos
      renderizarTarjetasFirmantes()
    })
    
    // Añadir al contenedor
    try {
      firmantesCardsContainer.value.appendChild(card)
    } catch (error) {
      console.warn('[renderizarTarjetasFirmantes] Error al añadir tarjeta:', error.message)
    }
  })
}

// Funciones de eventos del mouse
const obtenerCoordenadasRelativas = (e) => {
  if (!imgPdf.value) return null
  
  const rect = imgPdf.value.getBoundingClientRect()
  const x = e.clientX - rect.left
  const y = e.clientY - rect.top
  const cw = imgPdf.value.clientWidth
  const ch = imgPdf.value.clientHeight
  
  // Evitar dibujar en los bordes donde hay controles
  if (y >= ch - 25 || x >= cw - 70) return null
  
  return { x, y, cw, ch }
}

const convertirAMilimetros = (px, py, ancho, alto, cw, ch) => {
  if (!imgPdf.value) return { x: 0, y: 0, ancho: 0, alto: 0 }
  
  // Obtener la página actual con sus viewports
  const currentPage = pages.value[paginaActual.value - 1]
  if (!currentPage || !currentPage.viewport || !currentPage.viewportOriginal) {
    return { x: 0, y: 0, ancho: 0, alto: 0 }
  }
  
  const viewportRenderizado = currentPage.viewport // Viewport con escala (píxeles renderizados)
  const viewportOriginal = currentPage.viewportOriginal // Viewport original (puntos del PDF)
  const iw = imgPdf.value.naturalWidth
  const ih = imgPdf.value.naturalHeight
  
  // Convertir píxeles de pantalla a píxeles del canvas/imagen renderizada
  // px, py: coordenadas desde la esquina superior izquierda de la imagen visible
  const pxCanvas = (px / cw) * iw
  const pyCanvas = (py / ch) * ih
  const anchoCanvas = (ancho / cw) * iw
  const altoCanvas = (alto / ch) * ih
  
  // Convertir píxeles del canvas renderizado a puntos del PDF
  // El viewport renderizado tiene las dimensiones en píxeles escalados
  // El viewport original tiene las dimensiones reales en puntos
  const escalaX = viewportOriginal.width / viewportRenderizado.width
  const escalaY = viewportOriginal.height / viewportRenderizado.height
  
  // Coordenadas en puntos del PDF desde la esquina superior izquierda de la página (0,0)
  // X: distancia desde el borde izquierdo de la página
  // Y: distancia desde el borde superior de la página
  const xPuntos = pxCanvas * escalaX
  const yPuntos = pyCanvas * escalaY
  // Ancho: se extiende hacia la derecha desde X
  // Alto: se extiende hacia abajo desde Y
  const anchoPuntos = anchoCanvas * escalaX
  const altoPuntos = altoCanvas * escalaY
  
  // Convertir puntos a milímetros
  // 1 punto = 1/72 pulgada = 25.4/72 mm
  const mmPorPunto = 25.4 / 72
  
  return {
    x: xPuntos * mmPorPunto,      // X en mm desde el borde izquierdo (0,0)
    y: yPuntos * mmPorPunto,      // Y en mm desde el borde superior (0,0)
    ancho: anchoPuntos * mmPorPunto, // Ancho en mm hacia la derecha desde X
    alto: altoPuntos * mmPorPunto    // Alto en mm hacia abajo desde Y
  }
}

const onMouseDown = (e) => {
  if (!imgPdf.value || !overlayContainer.value) return
  
  const idx = numFirmante.value
  if (!props.firmantes[idx]) return
  
  const coords = obtenerCoordenadasRelativas(e)
  if (!coords) return
  
  e.preventDefault()
  e.stopPropagation()
  
  dibujandoRectangulo.value = true
  inicioRectangulo.value = { x: coords.x, y: coords.y }
  previewRect.value = { x: coords.x, y: coords.y, width: 0, height: 0 }
}

const onMouseMove = (e) => {
  if (!dibujandoRectangulo.value || !imgPdf.value) return
  
  const coords = obtenerCoordenadasRelativas(e)
  if (!coords) return
  
  e.preventDefault()
  e.stopPropagation()
  
  // Calcular dimensiones del rectángulo
  const startX = inicioRectangulo.value.x
  const startY = inicioRectangulo.value.y
  const currentX = coords.x
  const currentY = coords.y
  
  const x = Math.min(startX, currentX)
  const y = Math.min(startY, currentY)
  const width = Math.abs(currentX - startX)
  const height = Math.abs(currentY - startY)
  
  // Actualizar preview solo si el rectángulo tiene un tamaño mínimo
  if (width > 5 && height > 5) {
    previewRect.value = { x, y, width, height }
  }
}

const onMouseUp = (e) => {
  if (!dibujandoRectangulo.value || !imgPdf.value) return
  
  const coords = obtenerCoordenadasRelativas(e)
  if (!coords) return
  
  e.preventDefault()
  e.stopPropagation()
  
  const idx = numFirmante.value
  if (!props.firmantes[idx]) {
    dibujandoRectangulo.value = false
    return
  }
  
  // Calcular dimensiones finales del rectángulo
  // X, Y: esquina superior izquierda del cuadrado de firma
  const startX = inicioRectangulo.value.x
  const startY = inicioRectangulo.value.y
  const currentX = coords.x
  const currentY = coords.y
  
  // Asegurar que X, Y sean la esquina superior izquierda (mínimos)
  const x = Math.min(startX, currentX)
  const y = Math.min(startY, currentY)
  // Ancho: se extiende hacia la derecha desde X
  // Alto: se extiende hacia abajo desde Y
  const width = Math.abs(currentX - startX)
  const height = Math.abs(currentY - startY)
  
  // Solo guardar si el rectángulo tiene un tamaño mínimo
  if (width > 5 && height > 5) {
    const cw = imgPdf.value.clientWidth
    const ch = imgPdf.value.clientHeight
    
    // Convertir a milímetros
    // X, Y: coordenadas en mm desde la esquina superior izquierda de la página (0,0)
    // Ancho: se extiende hacia la derecha desde X
    // Alto: se extiende hacia abajo desde Y
    const mm = convertirAMilimetros(x, y, width, height, cw, ch)
    
    const firmanteId = props.firmantes[idx].firmante.id
    const nuevaPosicion = {
      x: x,
      y: y,
      ancho: width,
      alto: height,
      px: x,
      py: y,
      pagina: paginaActual.value,
      xMm: mm.x,
      yMm: mm.y,
      anchoMm: mm.ancho,
      altoMm: mm.alto
    }
    
    // Actualizar el mapa directamente
    posicionesMap.value.set(firmanteId, nuevaPosicion)
    
    // Renderizar marcadores y tarjetas de forma asíncrona
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (overlayContainer.value && imgPdf.value && overlayContainer.value.parentNode) {
          renderizarMarcadores()
        }
        // Re-renderizar tarjetas para actualizar el estado visual
        renderizarTarjetasFirmantes()
      })
    })
    
    emit('posicion-guardada', {
      firmanteId,
      posicion: nuevaPosicion
    })
    
    if (imgPdf.value) {
      imgPdf.value.style.border = '3px solid rgb(64, 158, 255)'
    }
  }
  
  dibujandoRectangulo.value = false
  previewRect.value = { x: 0, y: 0, width: 0, height: 0 }
}

const onMouseLeave = () => {
  // Si se sale del área mientras se dibuja, cancelar el dibujo
  if (dibujandoRectangulo.value) {
    dibujandoRectangulo.value = false
    previewRect.value = { x: 0, y: 0, width: 0, height: 0 }
  }
}

// Funciones de selección
const seleccionarFirmante = (index) => {
  numFirmante.value = index
  if (imgPdf.value && props.firmantes[index]) {
    const firmanteId = props.firmantes[index].firmante.id
    const tienePosicion = posicionesMap.value.get(firmanteId)
    imgPdf.value.style.border = tienePosicion ? '3px solid rgb(64, 158, 255)' : '3px solid darkgray'
  }
  // Re-renderizar tarjetas para actualizar estilos de selección
  renderizarTarjetasFirmantes()
}

const limpiarSeleccion = () => {
  posicionesMap.value.clear()
  numFirmante.value = 0
  
  if (imgPdf.value) {
    imgPdf.value.style.border = '3px solid darkgray'
  }
  
  // Limpiar marcadores del DOM
  if (overlayContainer.value) {
    while (overlayContainer.value.firstChild) {
      overlayContainer.value.removeChild(overlayContainer.value.firstChild)
    }
  }
  
  // Re-renderizar tarjetas para actualizar el estado visual
  renderizarTarjetasFirmantes()
  
  emit('posiciones-limpias')
}

// NOTA: guardarPosicionFirmante ya no es necesario porque se eliminó el botón "Guardar posición"

// Exponer métodos y datos para el componente padre
defineExpose({
  obtenerPosiciones: () => {
    const posiciones = []
    props.firmantes.forEach(item => {
      const posicion = posicionesMap.value.get(item.firmante.id)
      if (posicion) {
        posiciones.push({
          ...item,
          posicion: posicion
        })
      }
    })
    return posiciones
  },
  todasPosicionesListas: computed(() => {
    return props.firmantes.length > 0 &&
      props.firmantes.every(p => posicionesMap.value.get(p.firmante.id))
  }),
  cargarPdf
})

// Watch para recargar cuando cambian los firmantes
watch(() => props.firmantes, () => {
  if (props.firmantes.length > 0 && !pdfCargado.value) {
    cargarPdf()
  }
  // Re-renderizar tarjetas cuando cambian los firmantes
  nextTick(() => {
    renderizarTarjetasFirmantes()
  })
}, { immediate: true })

// Lifecycle
onMounted(() => {
  if (props.firmantes.length > 0) {
    cargarPdf()
  }
})

onBeforeUnmount(() => {
  // Preview deshabilitado - no hay limpieza necesaria
})
</script>

<style scoped>
.firmante-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.pdf-container {
  display: inline-block;
  position: relative;
}
</style>

